import { useEffect, useState } from 'react';

import { ROLE_OPTIONS_TYPE } from 'bundles/onboarding-common/constants';
import { QUESTION_IDS } from 'bundles/onboarding-common/constants/questionScheme';
import useFetchData from 'bundles/onboarding-common/hooks/useFetchData';
import useGetRolesByIds from 'bundles/onboarding-common/hooks/useGetRolesByIds';
import useOccupationPreferencesData from 'bundles/onboarding-common/hooks/useOccupationPreferencesData';
import type { RoleWithState } from 'bundles/onboarding-common/types';

const useGetDefaultSavedRoles = ({ skip }: { skip?: boolean }) => {
  const { value: savedValue, loading: isLoadingSavedValue } = useFetchData({
    useQueryData: useOccupationPreferencesData,
    questionIdForLocalStorage: QUESTION_IDS.roleSelect,
  });
  const [isLoadingDefaultRoles, setIsLoadingDefaultRoles] = useState(true);
  const [defaultValue, setDefaultValue] = useState<RoleWithState[]>([]);
  const { getRolesByIds } = useGetRolesByIds();

  useEffect(() => {
    const getDefaultSavedRoles = async () => {
      if (!isLoadingSavedValue && savedValue && savedValue.length > 0) {
        setIsLoadingDefaultRoles(true);

        const roleIds = savedValue.filter((role) => role.type === ROLE_OPTIONS_TYPE.DEFAULT).map((role) => role.id);
        const customRoles = savedValue
          .filter((role) => role.type === ROLE_OPTIONS_TYPE.CUSTOM)
          .map(({ id, name }) => ({
            id,
            name,
            slug: id,
            type: ROLE_OPTIONS_TYPE.CUSTOM,
            isVisible: false,
            isSearchResult: true,
            isSelected: true,
            isPartOfSearch: false,
          }));

        const { data } = await getRolesByIds({ variables: { ids: roleIds } });
        const defaultRoles =
          data?.PathwaySkillCareerRoles?.queryRolesByIds.map((role) => ({
            ...role,
            type: ROLE_OPTIONS_TYPE.DEFAULT,
            isVisible: true,
            isSelected: true,
            isSearchResult: false,
            isPartOfSearch: false,
          })) || [];

        setDefaultValue([...defaultRoles, ...customRoles]);
        setIsLoadingDefaultRoles(false);
      } else if (!isLoadingSavedValue) {
        setIsLoadingDefaultRoles(false);
      }
    };

    if (skip) {
      setIsLoadingDefaultRoles(false);
    } else {
      getDefaultSavedRoles();
    }
    // We only want to run this effect when the saved value is returned.
    // Unfortunately, the saved value is an array so we can't use it as a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getRolesByIds, isLoadingSavedValue, skip]);

  return {
    defaultValue,
    isLoadingDefaultRoles,
    savedValue,
    isLoadingSavedValue,
  };
};

export default useGetDefaultSavedRoles;
